import type {TFunction} from 'i18next'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {formatCopFull} from '@/lib/money'

/** Wompi's floor on a transaction total (base + IVA); mirrors backend `WOMPI_MIN_TOTAL_COP`.
 * $0 is the only legal value below it: a free entry never requests a payment link. */
const WOMPI_MIN_TOTAL_COP = 1500

/** Same rounding as the backend's `invoice_line_amounts` (nearest peso, never truncated). */
function priceBookTotalCop(amountCop: number, ivaPct: number): number {
  return amountCop + Math.round((amountCop * ivaPct) / 100)
}

/** `ivaPct` is per entry: the same base can pass or fail depending on its IVA, so never pass a fixed 19. */
export function wompiMinimumProblem(t: TFunction, amountCop: number, ivaPct: number): string | null {
  if (!Number.isFinite(amountCop) || !Number.isFinite(ivaPct) || amountCop <= 0) return null
  const total = priceBookTotalCop(amountCop, ivaPct)
  if (total < WOMPI_MIN_TOTAL_COP) {
    return t('cartera.precios.totalBelowWompiMinimum', {
      total: formatCopFull(total),
      min: formatCopFull(WOMPI_MIN_TOTAL_COP),
    })
  }
  const ivaCop = total - amountCop
  if (ivaCop * 2 >= total) {
    return t('cartera.precios.ivaShareTooHigh', {total: formatCopFull(total)})
  }
  return null
}

/** Includes Wompi's floor so a pair the gateway would 422 on never reaches submit. */
export function validateAmountIva(
  t: TFunction,
  amount: string,
  iva: string
): {amountNum: number; ivaNum: number} | {error: string} {
  const amountNum = Number(amount)
  if (!amount.trim() || !Number.isFinite(amountNum) || amountNum < 0) {
    return {error: t('cartera.precios.amountInvalid')}
  }
  const ivaNum = Number(iva)
  if (!iva.trim() || !Number.isFinite(ivaNum) || ivaNum < 0) {
    return {error: t('cartera.precios.ivaInvalid')}
  }
  const wompiProblem = wompiMinimumProblem(t, Math.round(amountNum), Math.round(ivaNum))
  if (wompiProblem) return {error: wompiProblem}
  return {amountNum, ivaNum}
}

export function AmountIvaFields({
  idPrefix,
  amount,
  iva,
  onAmountChange,
  onIvaChange,
  onBlur,
}: {
  idPrefix: string
  amount: string
  iva: string
  onAmountChange: (value: string) => void
  onIvaChange: (value: string) => void
  // Validates the amount+IVA pair (Wompi's floor needs both) on either field's blur (R4/R5).
  onBlur?: () => void
}) {
  const {t} = useTranslation()
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-amount`}>{t('cartera.precios.amountLabel')}</Label>
        <Input
          id={`${idPrefix}-amount`}
          data-testid={`${idPrefix}-amount`}
          type="number"
          min={0}
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          onBlur={onBlur}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-iva`}>{t('cartera.precios.ivaLabel')}</Label>
        <Input
          id={`${idPrefix}-iva`}
          data-testid={`${idPrefix}-iva`}
          type="number"
          min={0}
          value={iva}
          onChange={(e) => onIvaChange(e.target.value)}
          onBlur={onBlur}
        />
      </div>
    </>
  )
}
